import {useMemo} from 'react';

type JwtPayload = {
    sub?: string;
    exp?: number;
};

const base64UrlDecode = (segment: string): string => {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    return atob(normalized);
};

const jwtParsePayload = (token: string): JwtPayload | null => {
    const segments = token.split('.');

    if (segments.length < 2) {
        return null;
    }

    try {
        const payload = JSON.parse(base64UrlDecode(segments[1])) as JwtPayload;
        return payload ?? null;
    } catch {
        return null;
    }
};

const getJwtSubject = (token: string): string | null => {
    const payload = jwtParsePayload(token);
    return typeof payload?.sub === 'string' ? payload.sub : null;
};

export const useJwtSubject = (token: string): string | null => {
    return useMemo(() => getJwtSubject(token), [token]);
};

const getJwtExpiry = (token: string): number | null => {
    const payload = jwtParsePayload(token);
    return typeof payload?.exp === 'number' ? payload.exp : null;
};

// A token we cannot read, or one with no exp claim, is not safe to reuse. Both
// count as expired and send the user back to sign-in.
export const isJwtExpired = (token: string): boolean => {
    const exp = getJwtExpiry(token);

    if (exp === null) {
        return true;
    }

    return exp * 1000 <= Date.now();
};
